/**
 * Handwriting text from a reader or from the learner, shown so that what is on
 * screen is what is stored.
 *
 * Two things can make the screen say something other than the stored text, and
 * a "Yes, this is what I wrote" on either would confirm text the learner never
 * saw:
 *
 * 1. **Invisible characters.** Some characters have no glyph and exist to change
 *    how their neighbours are drawn or to hide in a string: the bidirectional
 *    controls, zero-width characters, the invisible operators (U+2061 to
 *    U+2064: "2<U+2062>3" is drawn as "23"), the soft hyphen, variation
 *    selectors, tag characters. Every `Default_Ignorable_Code_Point`, plus the
 *    bidi controls (U+200E, U+200F, U+202A to U+202E, U+2066 to U+2069, U+061C),
 *    is drawn as a visible marker naming its code point. This is a superset of
 *    `printable()` in `apps/server/src/admin/course-commands.ts`.
 *
 * 2. **Bidi reordering of digits.** Measured in Chromium, not assumed, twice:
 *    - A fixed `dir="ltr"` on the line, even with `unicode-bidi: isolate`, still
 *      draws the Hebrew-led text "3 - 5" as "5 - 3": digits after a Hebrew
 *      letter join the right-to-left run and are reversed.
 *    - Isolating only Hebrew phrases (`<bdi dir="ltr">` for the rest) was still
 *      not enough: every other right-to-left script (Arabic, Syriac, Thaana,
 *      NKo, Samaritan, the presentation forms, U+10800 and U+1E800 onward - some
 *      near-invisible and not default-ignorable, like U+070F) reorders "5 - 3"
 *      the same way, Arabic-Indic digits reverse with no letter at all, and
 *      brackets are mirrored. `bdi` isolates but does not stop the algorithm
 *      inside the run.
 *    A reader that emitted "5 - 3" for paper that reads "3 - 5" would be shown
 *    "3 - 5", matching the paper, and "Yes, this is what I wrote" would confirm
 *    it: a reversed operand is a false accept. So the text is split into Hebrew
 *    phrases and everything else (`bidiSegments`); a Hebrew phrase is
 *    `<bdi dir="rtl">` (its words keep their Hebrew order, and Hebrew is the one
 *    RTL script the learners write), and EVERY other run - including text with
 *    no Hebrew at all - is `<bdo dir="ltr">`, whose `unicode-bidi:
 *    isolate-override` draws its characters strictly in stored order whatever
 *    their script. The runs follow one another in logical order in a line
 *    whose own direction (`HandwrittenText`) is right to left when it holds a
 *    Hebrew letter, else left to right. The stylesheet cannot do this alone; `e2e/bidi.spec.ts`
 *    measures it in a browser.
 *
 *    Accepted consequence: words in an RTL script other than Hebrew are drawn
 *    letter-reversed. That is visible garble, which the learner corrects - a
 *    false reject, which costs a retype - and the alternative is drawing digits
 *    in an order that is not the stored one, which is a false accept.
 *
 * Display only. The text that is stored, sent and compared is never changed,
 * stripped or normalised here: markers, `<bdi>` and `<bdo>` runs exist in the
 * DOM and nowhere else.
 */

/** A Hebrew letter or sign: the Hebrew block and the Hebrew presentation forms. */
const HEBREW = String.raw`\u0590-\u05FF\uFB1D-\uFB4F`;
/**
 * A Hebrew phrase: starts and ends with a Hebrew character and holds only Hebrew
 * characters, spaces and the punctuation Hebrew writing uses between them
 * (geresh, gershayim and maqaf are in the Hebrew block already; ' " , .).
 */
const HEBREW_PHRASE = new RegExp(`[${HEBREW}](?:[${HEBREW} '",.]*[${HEBREW}])?`, 'gu');

/** Every default-ignorable code point, plus the bidi controls that are not (U+200E/F, U+202A-E, U+2066-9, U+061C). */
const INVISIBLE = new RegExp(
  String.raw`(\p{Default_Ignorable_Code_Point}|[\u200B-\u200F\u202A-\u202E\u2060\u2066-\u2069\u061C\uFEFF])`,
  'u',
);

/** The base direction of a line: right to left when it holds a Hebrew letter, else left to right. */
export function lineDir(text: string): 'rtl' | 'ltr' {
  return new RegExp(`[${HEBREW}]`, 'u').test(text) ? 'rtl' : 'ltr';
}

export interface BidiSegment {
  readonly kind: 'rtl' | 'ltr';
  readonly text: string;
}

/**
 * The text as runs that can each be drawn in isolation: Hebrew phrases (`rtl`)
 * and everything between them (`ltr`), in logical order. Concatenating the
 * segments gives the text back exactly.
 */
export function bidiSegments(text: string): BidiSegment[] {
  const segments: BidiSegment[] = [];
  let last = 0;
  for (const match of text.matchAll(HEBREW_PHRASE)) {
    const start = match.index;
    let end = start + match[0].length;
    // A hyphen joins the Hebrew run only as the hyphen of a one-letter prefix
    // ("ו-q", "ב-A"): the phrase is exactly one letter, with no letter or digit
    // before it, and the next character is neither a space nor a digit. Anywhere
    // else it stays with the maths and is drawn as a visible minus ("שווה-5",
    // "ו-5", "שווה-q", "יהי -q"): a hyphen hidden in the Hebrew run would turn a
    // stored minus into a connective the learner confirms (a false accept),
    // while a minus that looks odd is one the learner corrects (a false reject).
    const prev = start > 0 ? text[start - 1]! : '';
    const next = text[end + 1] ?? '';
    if (
      match[0].length === 1 &&
      text[end] === '-' &&
      !/[\p{L}\p{N}]/u.test(prev) &&
      next !== '' &&
      !/[\s\p{N}]/u.test(next)
    ) {
      end += 1;
    }
    if (start > last) segments.push({ kind: 'ltr', text: text.slice(last, start) });
    segments.push({ kind: 'rtl', text: text.slice(start, end) });
    last = end;
  }
  if (last < text.length) segments.push({ kind: 'ltr', text: text.slice(last) });
  return segments;
}

/** "U+202E", "U+E0041" - the form a person can look up. */
export function codePointLabel(char: string): string {
  return `U+${(char.codePointAt(0) ?? 0).toString(16).toUpperCase().padStart(4, '0')}`;
}

/** One run's text, each invisible character (one code point, astral included) shown as a marker. */
function withMarkers(text: string): React.JSX.Element {
  // `split` with a unicode-flag regex and one capture group puts every matched
  // code point at an odd index.
  const parts = text.split(INVISIBLE);
  if (parts.length === 1) return <>{text}</>;
  return (
    <>
      {parts.map((part, index) =>
        index % 2 === 1 ? (
          <span key={index} className="invisible-char" title={codePointLabel(part)}>
            {`⟨${codePointLabel(part)}⟩`}
          </span>
        ) : (
          part
        ),
      )}
    </>
  );
}

/**
 * A left-to-right run as an isolate-override `<bdo>`, its leading and trailing
 * blanks outside it as plain text: inside, a trailing space draws on the outer
 * side and "1." would touch the Hebrew word after it. Concatenated, the pieces
 * are the run exactly.
 */
function LtrRun({ text }: { readonly text: string }): React.JSX.Element {
  const lead = /^[ \t\n]*/u.exec(text)![0];
  const trail = lead.length === text.length ? '' : /[ \t\n]*$/u.exec(text)![0];
  const core = text.slice(lead.length, text.length - trail.length);
  if (core === '') return <>{text}</>;
  return (
    <>
      {lead}
      <bdo dir="ltr">{withMarkers(core)}</bdo>
      {trail}
    </>
  );
}

/**
 * The text as React children: each Hebrew phrase an isolated `<bdi dir="rtl">`,
 * every other run - always wrapped, even when the whole text is one run - a
 * `<bdo dir="ltr">` that overrides the bidi algorithm, invisible characters
 * shown as markers inside whichever run they are in.
 */
export function UntrustedText({ text }: { readonly text: string }): React.JSX.Element {
  return (
    <>
      {bidiSegments(text).map((segment, index) =>
        segment.kind === 'rtl' ? (
          <bdi key={index} dir="rtl">
            {withMarkers(segment.text)}
          </bdi>
        ) : (
          <LtrRun key={index} text={segment.text} />
        ),
      )}
    </>
  );
}

/**
 * Untrusted text in a block that keeps its whitespace. A line with a Hebrew
 * letter is `dir="rtl"` (#647: under a left-to-right base the line's first run
 * is drawn at the wrong end), any other line `dir="ltr"`. Its runs are isolated
 * by `UntrustedText`; the line's own `dir` alone would NOT keep digits in order
 * (see above), so every non-Hebrew run stays an isolate-override `<bdo
 * dir="ltr">` (stronger than the `<bdi>` #647 first proposed, for the reasons
 * above) and the neutrals between runs resolve against the line.
 */
export function HandwrittenText({ text }: { readonly text: string }): React.JSX.Element {
  return (
    <p className="handwriting" dir={lineDir(text)}>
      <UntrustedText text={text} />
    </p>
  );
}
